/**
 * LocationPicker — select a branch when a menu has multiple locations.
 *
 * Behavior:
 * - 0 locations → renders nothing (backward compatible)
 * - 1 location  → auto-selects, renders nothing
 * - 2+ locations → shows a picker sorted by distance (nearest first)
 */

import { useEffect, useMemo } from 'react'
import { MapPin, Navigation } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setSelectedLocationId } from '@/store/orderSlice'
import { useGeolocation } from '@/hooks/useGeolocation'
import { haversineDistance, formatDistance } from '@/utils/distance'
import type { MenuLocation } from '@/hooks/useMenuData'

interface LocationPickerProps {
  locations: MenuLocation[]
}

export function LocationPicker({ locations }: LocationPickerProps) {
  const dispatch = useAppDispatch()
  const selectedId = useAppSelector((s) => s.order.selectedLocationId)
  const { coords: userCoords, isGranted } = useGeolocation()

  // Auto-select single location
  useEffect(() => {
    if (locations.length === 1 && selectedId !== locations[0].id) {
      dispatch(setSelectedLocationId(locations[0].id))
    }
  }, [locations, selectedId, dispatch])

  // Sort by distance when geolocation is available
  const sorted = useMemo(() => {
    if (!userCoords || !isGranted) return locations

    return [...locations].sort((a, b) => {
      const distA =
        a.latitude != null && a.longitude != null
          ? haversineDistance(userCoords, { lat: a.latitude, lng: a.longitude })
          : Infinity
      const distB =
        b.latitude != null && b.longitude != null
          ? haversineDistance(userCoords, { lat: b.latitude, lng: b.longitude })
          : Infinity
      return distA - distB
    })
  }, [locations, userCoords, isGranted])

  // Nothing to render for 0 or 1 locations
  if (locations.length <= 1) return null

  return (
    <div className="space-y-2">
      <p className="text-sm font-semibold">Choose a location</p>
      <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
        {sorted.map((loc) => {
          const isSelected = selectedId === loc.id
          const dist =
            isGranted && userCoords && loc.latitude != null && loc.longitude != null
              ? haversineDistance(userCoords, { lat: loc.latitude, lng: loc.longitude })
              : null

          const addressParts = [loc.address, loc.city, loc.state, loc.postal_code].filter(Boolean)

          return (
            <button
              key={loc.id}
              type="button"
              onClick={() => dispatch(setSelectedLocationId(loc.id))}
              className={`w-full text-left flex items-start gap-3 rounded-xl border-2 px-4 py-3 transition-all active:scale-[0.98] ${
                isSelected
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-foreground/30 hover:bg-muted/50'
              }`}
            >
              <MapPin className={`h-4 w-4 mt-0.5 shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{loc.location_name}</p>
                {addressParts.length > 0 && (
                  <p className="text-xs text-muted-foreground truncate">
                    {addressParts.join(', ')}
                  </p>
                )}
              </div>
              {dist != null && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0 mt-0.5">
                  <Navigation className="h-3 w-3" />
                  {formatDistance(dist)}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
